import React from "react";
import { Box, Typography } from "@mui/material";

/* ================================
   LEVELS (no Level 4 for now)
   ================================ */

const LEVELS = [
  { key: "1", label: "Foundations" },
  { key: "1-2", label: "Developing" },
  { key: "2", label: "Intermediate" },
  { key: "2-3", label: "Advanced" },
  { key: "3", label: "Proficient" },
];

/* ================================
   STYLE
   ================================ */

const BAR_BG = "rgba(0,0,0,0.08)";

const FULL_BAR_GRADIENT =
  "linear-gradient(90deg, #d32f2f 0%, #f57c00 30%, #fbc02d 55%, #7cb342 78%, #2e7d32 100%)";

/* ================================
   HELPERS
   ================================ */

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const normalizeLevelKey = (lvl) => {
  if (lvl === null || lvl === undefined) return "1";
  const s = String(lvl).trim();

  const nameMap = {
    foundations: "1",
    developing: "1-2",
    intermediate: "2",
    advanced: "2-3",
    proficient: "3",
  };
  if (nameMap[s]) return nameMap[s];

  if (LEVELS.some((l) => l.key === s)) return s;

  const cleaned = s.replace(".0", "");
  if (LEVELS.some((l) => l.key === cleaned)) return cleaned;

  return "1";
};

/* ================================
   COMPONENT
   ================================ */

const SkillLevelProgressRow = ({
  topic,
  currentLevel = "1",
  progress = 0.3, // 0 → 1 within current level
  labelWidth = 300,
  barHeight = 18,
}) => {
  const levelKey = normalizeLevelKey(currentLevel);
  const levelIndex = LEVELS.findIndex((l) => l.key === levelKey);

  const totalProgress =
    levelIndex < 0 ? 0 : (levelIndex + clamp01(progress)) / LEVELS.length;

  const earnedPct = clamp01(totalProgress) * 100;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        minHeight: 32,
      }}
    >
      {/* Topic label */}
      <Box
        sx={{
          width: labelWidth,
          maxHeight: 32,
          overflow: "hidden",
        }}
      >
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            lineHeight: 1.15,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
          }}
        >
          {topic}
        </Typography>
      </Box>

      {/* Bar */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          height: barHeight,
          borderRadius: barHeight / 2,
          background: BAR_BG,
          overflow: "hidden",
        }}
      >
        {/* Gradient exists FULL width, but is CLIPPED to earned% (no bleed possible) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: FULL_BAR_GRADIENT,
            clipPath: `inset(0 ${100 - earnedPct}% 0 0)`,
            transition: "clip-path 0.35s ease",
            willChange: "clip-path",
            zIndex: 1,
          }}
        />

        {/* Segment dividers */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {LEVELS.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                flex: 1,
                borderRight:
                  idx < LEVELS.length - 1
                    ? "2px solid rgba(255,255,255,0.9)"
                    : "none",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SkillLevelProgressRow;
